import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, MessageCircle, Send } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import api, { formatApiError } from "../lib/api";
import { Overline, Reveal } from "./Reveal";

export default function ContactSection() {
  const { t } = useI18n();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ nombre: "", email: "", telefono: "", mensaje: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api
      .get("/settings")
      .then((r) => setSettings(r.data || {}))
      .catch(() => setSettings({}));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t.contacto.form.success);
      setForm({ nombre: "", email: "", telefono: "", mensaje: "" });
    } catch (err) {
      toast.error(formatApiError(err, t.contacto.form.error));
    } finally {
      setSending(false);
    }
  };

  const waNumber = (settings.whatsapp || "").replace(/[^\d]/g, "");

  return (
    <section id="contacto" data-testid="contact-section" className="bg-cream py-24 text-charcoal md:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Overline>{t.contacto.overline}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">
              {t.contacto.title}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-md leading-relaxed text-charcoal/70">{t.contacto.lead}</p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="mt-8 inline-flex items-center gap-2 text-sm text-charcoal/70">
              <MapPin size={16} className="text-terra" />
              {t.contacto.location}
            </p>
          </Reveal>
          {settings.email && (
            <Reveal delay={0.26}>
              <p className="mt-3 text-sm text-charcoal/70">{settings.email}</p>
            </Reveal>
          )}
          {settings.telefono && (
            <Reveal delay={0.3}>
              <p className="mt-1 text-sm text-charcoal/70">{settings.telefono}</p>
            </Reveal>
          )}
          {waNumber && (
            <Reveal delay={0.34}>
              <a
                data-testid="whatsapp-direct-button"
                href={`https://wa.me/${waNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-3 rounded-full bg-olive px-6 py-3.5 text-sm font-medium text-cream transition-all duration-300 hover:scale-[1.02] hover:opacity-90"
              >
                <MessageCircle size={17} />
                {t.contacto.whatsapp}
              </a>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.15} y={40}>
            <form data-testid="contact-form" onSubmit={submit} className="border border-charcoal/12 bg-white/70 p-7 md:p-10">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="field-label" htmlFor="contact-nombre">{t.contacto.form.nombre} *</label>
                  <input
                    id="contact-nombre"
                    data-testid="contact-field-nombre"
                    className="field"
                    required
                    value={form.nombre}
                    onChange={(e) => set("nombre", e.target.value)}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="contact-email">{t.contacto.form.email} *</label>
                  <input
                    id="contact-email"
                    data-testid="contact-field-email"
                    type="email"
                    className="field"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-6">
                <label className="field-label" htmlFor="contact-telefono">{t.contacto.form.telefono}</label>
                <input
                  id="contact-telefono"
                  data-testid="contact-field-telefono"
                  type="tel"
                  className="field"
                  value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value)}
                />
              </div>
              <div className="mt-6">
                <label className="field-label" htmlFor="contact-mensaje">{t.contacto.form.mensaje} *</label>
                <textarea
                  id="contact-mensaje"
                  data-testid="contact-field-mensaje"
                  className="field min-h-32 resize-y"
                  required
                  value={form.mensaje}
                  onChange={(e) => set("mensaje", e.target.value)}
                />
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-charcoal px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:bg-terra disabled:opacity-60"
              >
                {sending ? t.contacto.form.submitting : t.contacto.form.submit}
                {!sending && <Send size={15} />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
